import CustomButton from "@components/buttons/CustomButton";
import { MerchantDropdown } from "@components/dropdowns/MerchantDropdown";
import SlidingPanel from "@components/sliders/SlidingPanel";
import { primeServices } from "@services/apis/Prime";
import { NtxExpireTradeType } from "@services/models/Prime/ExpireNtxRampTradesModel";
import { useEffect, useMemo, useState } from "react";
import { useMediaQuery } from "react-responsive";
import useSnackbarMessage from "../../../utils/HandleSuccessErrorSnackbars";

const MINUTE_OPTIONS = Array.from({ length: 10 }, (_, index) => {
  const minutes = index + 1;
  return { id: minutes, name: `${minutes} min` };
});

const TRADE_TYPE_OPTIONS: { id: NtxExpireTradeType; label: string }[] = [
  { id: "payin", label: "Pay-in" },
  { id: "payout", label: "Pay-out" },
  { id: "both", label: "Both" },
];

interface ExpireNtxRampTradesSliderProps {
  isOpen: boolean;
  onClose: () => void;
}

const ExpireNtxRampTradesSlider = ({
  isOpen,
  onClose,
}: ExpireNtxRampTradesSliderProps) => {
  const { handleError, handleSuccess } = useSnackbarMessage();
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const minuteOptions = useMemo(() => MINUTE_OPTIONS, []);
  const [minutes, setMinutes] = useState(5);
  const [tradeType, setTradeType] = useState<NtxExpireTradeType>("both");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setMinutes(5);
    setTradeType("both");
  }, [isOpen]);

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const runExpiry = async () => {
    try {
      setIsSubmitting(true);
      const response = await primeServices.expireNtxRampTrades({
        minutes,
        tradeType,
      });
      handleSuccess({
        message:
          response.msg ||
          `Successfully expired ${response.data.affectedRows} NTX ramp trades.`,
      });
      onClose();
    } catch (err: any) {
      handleError({
        message:
          err?.response?.data?.msg || "Failed to expire NTX ramp trades.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SlidingPanel
      isOpen={isOpen}
      heading="Expire NTX Ramp Trades"
      onClose={handleClose}
      onClickCross={handleClose}
      showConfirm={false}
      showCross={!isSubmitting}
      isMobile={isMobile}
    >
      <div
        className={`flex flex-col gap-4 p-3 ${isMobile ? "min-h-[calc(80vh-100px)]" : ""}`}
      >
        <p className="text-text-base text-[13px] font-normal leading-5">
          Bulk-expire in-flight NTX ramp trades created at least {minutes}{" "}
          minutes ago. This targets trades still processing (event IDs 1, 2, 3,
          5, 6, 7, and 8) and sets each event deadline to now.
        </p>

        <div className="flex flex-col gap-2 rounded-lg border border-border-subtle bg-bg-base px-3 py-3">
          <p className="text-text-subtle text-[11px] font-medium">
            How old should matching trades be?
          </p>
          <MerchantDropdown
            listOfValues={minuteOptions}
            value={minutes}
            onChange={(val: any) => setMinutes(Number(val))}
            label="Select minutes"
            color="bg-main-container-header"
            baseColor="bg-bg-page-body"
            borderColor="border-subtle"
            expandedBorderColor="border-button-primary"
          />
          <p className="text-text-subtle text-[11px]">
            Trades created at least{" "}
            <span className="text-text-base font-semibold">
              {minutes} minutes
            </span>{" "}
            ago
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <p className="text-text-subtle text-[11px] font-medium">Trade flow</p>
          <div className="flex gap-2">
            {TRADE_TYPE_OPTIONS.map((option) => {
              const isSelected = tradeType === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => setTradeType(option.id)}
                  className={`flex-1 border py-[6px] px-3 text-[12px] font-medium rounded-[100px] disabled:opacity-50 ${
                    isSelected
                      ? "border-border-button-secondary bg-bg-button-secondary text-text-base"
                      : "border-border-subtle bg-bg-page-body text-text-base"
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>

        <CustomButton
          text="EXPIRE TRADES"
          isActive={true}
          onClick={runExpiry}
          isButtonLoading={isSubmitting}
          buttonColor="bg-bg-button-delete"
          borderColor="border-border-button-delete"
        />
      </div>
    </SlidingPanel>
  );
};

export default ExpireNtxRampTradesSlider;
